import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GTMTracking } from '@common/ui/general';
import { gtmEventHandler } from '@common/utils/gtm';

import { isUserLoggedIn, showReviewPageRevamp } from '@alumni_v2/utils/helpers';

import Card from '@alumni_v2/ui/Card';
import {
  fetchReviews,
  setIsPersonalisationModalOpen,
  setIsProfileSidebarModalOpen,
} from '@alumni_v2/stores/reviews/actions';
import LoadingState from '@alumni_v2/ui/LoadingState';
import {
  loadingSelector,
  paginationSelector,
  requestParamsSelector,
  testimonialsSelector,
  fcSelector,
} from '@alumni_v2/selectors/reviews';
import PlacementBanner
  from '@alumni_v2/ui/PlacementBannerV2/PlacementBanner';
import { useThrottled, useMediaQuery } from '@common/hooks';
import NotFound from '@alumni_v2/ui/Review/NotFound';
import QuestionBanner from '@alumni_v2/ui/QuestionBanner';
import AcsStrip from '../AcsStrip';
import SidebarModal from '@alumni_v2/ui/SidebarModal';

import scrollUpIcon from '@alumni_v2/images/svg/scrollUpIcon.svg';

import FCBanner from '../FcBanner';

import styles from './Review.module.scss';

function Review() {
  const dispatch = useDispatch();
  const { mobile } = useMediaQuery();
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [bannerClicked, setBannerClicked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 0: profile, 1: CRT, 2: SET, 3: Thank You
  const [sidebarModalStage, setSidebarModalStage] = useState(0);
  const [sidebarOpenProfile, setSidebarOpenProfile] = useState(null);

  const { showAlumniConnect } = window.__INITIAL_STATE__;

  useEffect(() => {
    const handleScrollToTopVisibility = () => {
      const shouldShow = window.scrollY > 500;
      setShowScrollToTop(shouldShow);
    };

    window.addEventListener('scroll', handleScrollToTopVisibility);
    return () => {
      window.removeEventListener('scroll', handleScrollToTopVisibility);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const questionBannerClick = useCallback(() => {
    setBannerClicked(true);
  }, []);

  useEffect(() => {
    if (bannerClicked) {
      handleScrollToTop();
      setBannerClicked(false);
    }
  }, [bannerClicked]);

  const requestParams = useSelector(requestParamsSelector) || {};
  const testimonials = useSelector(testimonialsSelector) || [];
  const pagination = useSelector(paginationSelector);
  const loading = useSelector(loadingSelector);
  const {
    fcEventDataError,
    fcEventData,
    isFcRegisteredAlready,
  } = useSelector(fcSelector);

  const handleScroll = useCallback(() => {
    const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
    const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 1000;
    if (
      isScrolledToBottom
      && pagination?.currentPage
      && pagination.currentPage < pagination?.totalPages
      && !loading
    ) {
      gtmEventHandler(
        'cta',
        'load_more_profiles',
        'click',
        'alumni_directory',
        {
          click_type: 'load_more_profiles',
          click_text: `page_${pagination.currentPage + 1}`,
        },
      );
      dispatch(fetchReviews(pagination.currentPage + 1));
    }
  }, [dispatch, pagination, loading]);

  const throttledHandleScroll = useThrottled(handleScroll, 1000);

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [throttledHandleScroll]);

  const urlParams = new URLSearchParams(window.location.search);
  const alumniFilters = urlParams.get('alumni_id');

  const hasQnAFilter = requestParams.quickFilters.includes('q&a_with_alumni');

  useEffect(() => {
    if (alumniFilters !== null) {
      setSidebarOpenProfile(alumniFilters);
      dispatch(setIsPersonalisationModalOpen(false));
    }
  }, [alumniFilters, dispatch]);

  const renderBannerExperiment = () => {
    if (showAlumniConnect) {
      if (isFcRegisteredAlready) return null;

      if (!isUserLoggedIn() || (!fcEventDataError
        && Object.keys(fcEventData).length !== 0)) {
        return (
          <div className="scaler vd-w-full">
            <FCBanner />
          </div>
        );
      }
      return null;
    } else if (!hasQnAFilter) {
      return <QuestionBanner onBannerClick={questionBannerClick} />;
    }

    return null;
  };

  const sidebarModalData = useMemo(() => {
    const data = {};
    if (testimonials.length > 0) {
      const alumniData = testimonials.find(
        alumni => alumni.attributes.user_id === Number(sidebarOpenProfile),
      );

      if (alumniData) {
        const { attributes } = alumniData;
        data.name = attributes.name;
        data.profileImg = attributes.image_url;
        data.preScalerRole = attributes.pre_scaler_role;
        data.designation = attributes.role;
        data.location = attributes.city || attributes.region;
        data.preScalerText = attributes.pre_academy_company;
        data.preScalerImg = attributes.pre_academy_company_logo_url;
        data.postScalerText = attributes.post_academy_company;
        data.postScalerImg = attributes.company_logo_url;
        data.salaryHike = attributes.growth;
        data.availableForQnA = attributes.eligible_for_qna;
        data.programDetails = `${attributes.program} ${attributes.batch}`;
        data.linkedinId = attributes.linkedin;
        data.linkedinPost = attributes.linkedin_blog;
        data.content = attributes.content;
        data.collegeName = attributes.college;
        data.deegree = attributes.degree;
        data.workExperience = attributes.experience
          ? `${attributes.experience} Years`
          : null;
        data.userSlug = attributes.user_slug;
        data.userId = attributes.user_id;
        data.videoBlog = attributes.video_blog;
        data.rating = attributes.rating;
      }
    }
    return data;
  }, [sidebarOpenProfile, testimonials]);

  const handleCloseModal = useCallback(() => {
    dispatch(setIsProfileSidebarModalOpen(false));
    setIsModalOpen(false);
  }, [setIsModalOpen, dispatch]);

  if (loading && testimonials.length === 0) {
    return <LoadingState />;
  } else if (
    (requestParams.search
      || requestParams.quickFilters.length > 0
      || Object.keys(requestParams.advancedFilters).length > 0)
    && testimonials.length === 0
  ) {
    return <NotFound />;
  } else {
    return (
      <div className={styles.container}>
        <div className={styles.review}>
          {testimonials.map(({ id, attributes, forceOpen }, index) => {
            const isFirstRow = hasQnAFilter && index === 0;
            const isFifthIndex = index === 5;
            const isFourtheenIndex = index === 14;

            return (
              <>
                {!showAlumniConnect
                  && isFirstRow
                  && <QuestionBanner onBannerClick={questionBannerClick} />}
                <Card
                  key={`${id}-${index}`}
                  name={attributes.name}
                  profileImg={attributes.image_url}
                  preScalerRole={attributes.pre_scaler_role}
                  designation={attributes.role}
                  location={attributes.city || attributes.region}
                  preScalerImg={attributes.pre_academy_company_logo_url}
                  preScalerText={attributes.pre_academy_company}
                  postScalerText={attributes.post_academy_company}
                  postScalerImg={attributes.company_logo_url}
                  salaryHike={attributes.growth}
                  availableForQnA={attributes.eligible_for_qna}
                  programDetails={`${attributes.program} ${attributes.batch}`}
                  linkedinId={attributes.linkedin}
                  linkedinPost={attributes.linkedin_blog}
                  content={attributes.content}
                  collegeName={attributes.college}
                  deegree={attributes.degree}
                  userSlug={attributes.user_slug}
                  userId={attributes.user_id}
                  videoBlog={attributes.video_blog}
                  rating={attributes.rating}
                  workExperience={
                    attributes.experience
                      ? `${attributes.experience} Years`
                      : null
                  }
                  forceOpen={forceOpen
                    || Number(alumniFilters) === attributes.user_id}
                  setSidebarModalStage={setSidebarModalStage}
                  setIsModalOpen={setIsModalOpen}
                  isModalOpen={isModalOpen}
                  setSidebarOpenProfile={setSidebarOpenProfile}
                />
                {isFifthIndex
                && !showReviewPageRevamp() && renderBannerExperiment()}
                {isFifthIndex
                && showReviewPageRevamp() && !mobile && <PlacementBanner />}
                {isFourtheenIndex
                && showReviewPageRevamp()
                && !mobile && renderBannerExperiment()}
              </>
            );
          })}
        </div>
        {isModalOpen
          && (
            <SidebarModal
              sidebarModalStage={sidebarModalStage}
              setSidebarModalStage={setSidebarModalStage}
              isModalOpen={isModalOpen}
              onModalClose={handleCloseModal}
              {...sidebarModalData}
            />
          )}
        {loading && testimonials.length ? (
          <LoadingState />
        ) : (
          <div className={styles.scrollMessage}>
            {testimonials.length > 0
              && !loading
              && pagination.currentPage === pagination?.totalPages && (
              <>
                <div>
                  You have reached the end, Try out different filters to view
                  more profiles
                </div>
                {showScrollToTop && (
                  <GTMTracking
                    component="button"
                    gtmEventType="cta"
                    gtmEventAction="click"
                    gtmEventResult="scroll_to_up"
                    gtmEventCategory="alumni_directory"
                    gtmEventData={{
                      click_type: 'scroll_to_up',
                    }}
                    className={styles.scrollToTopButton}
                    onClick={handleScrollToTop}
                  >
                    Scroll back to top
                    <img src={scrollUpIcon} alt="scrollUpIcon" />
                  </GTMTracking>
                )}
              </>
            )}
          </div>
        )}
        <AcsStrip />
      </div>
    );
  }
}

export default Review;
